// The interviewer packet pane (DC-170, DC-172): one packet's six promised contents in the kickoff email's order
// (G §10) — App access, Packet links, Assigned ROOTS/skills, Interview guidance, the 24-hour scorecard SLA and the
// confidentiality and debrief expectations. Reads V1 `packet.open`; the guide pages are the pinned guide version's
// content (configuration), drawn as the server gives them.

import { packetOpen, type PacketOpenOutput } from "@/api/tools";
import { useRead } from "@/api/useTool";
import {
  Card, Due, EmptyState, Flex, LEVEL_DISPLAY, McRoot, NAV_ITEM_LABELS, PageHeader, Pill, SCORE_TEMPLATES, SCREEN_STATUS, SLA_LABELS, Stack,
  TextLink, type Level,
} from "@/mc";
import { displayOf, dueTierOf } from "./Interviews";
import { KICKOFF_CONTENT_LABELS, SCORECARD_PAGE_LABELS, SCORE_LABELS } from "./labels";
import { hisRoundLabel } from "./round";

export type PacketSection = "appAccess" | "packetLinks" | "assignedRootsSkills" | "interviewGuidance" | "scorecardSla" | "confidentiality";

/** G §10's order; the test holds the pane to it. */
export const PACKET_SECTIONS: readonly PacketSection[] = [
  "appAccess", "packetLinks", "assignedRootsSkills", "interviewGuidance", "scorecardSla", "confidentiality",
];

export const SECTION_TITLE: Readonly<Record<PacketSection, string>> = {
  appAccess: KICKOFF_CONTENT_LABELS.appAccess,
  packetLinks: KICKOFF_CONTENT_LABELS.packetLinks,
  assignedRootsSkills: KICKOFF_CONTENT_LABELS.assignedRootsSkills,
  interviewGuidance: KICKOFF_CONTENT_LABELS.interviewGuidance,
  scorecardSla: SLA_LABELS.scorecardSla,
  confidentiality: KICKOFF_CONTENT_LABELS.confidentiality,
};

const LEVELS = new Set<string>(Object.keys(LEVEL_DISPLAY));

/** The guide's guidance lines: the pages' `guidance` (a line or lines), else the packet's reminders. */
export function guidanceOf(packet: Pick<PacketOpenOutput, "pages" | "reminders">): string[] {
  const raw = packet.pages?.["guidance"];
  if (typeof raw === "string" && raw) return [raw];
  if (Array.isArray(raw)) return raw.filter((line): line is string => typeof line === "string" && line.length > 0);
  return packet.reminders.filter((line) => line.length > 0);
}

/** B14 (DC-246): an assigned skill by its intake title from the pinned pages, never a bare number; the number's own
 *  word only when the pages carry no title (a projection without pages). */
export function skillWordsOf(packet: Pick<PacketOpenOutput, "pages" | "skill_numbers">): string[] {
  const pages = (packet.pages?.["skill_pages"] ?? []) as { number?: unknown; title?: unknown }[];
  return packet.skill_numbers.map((n) => {
    const title = pages.find((page) => Number(page.number) === n)?.title;
    return typeof title === "string" && title ? title : SCORE_TEMPLATES.skill({ n });
  });
}

/** A withdrawn application's row: its scorecard is not opened from the packet (`scorecard.open` refuses the cancelled
 * round), so the row shows the withdrawn state and links nowhere (demo smoke D6). */
export function isWithdrawnRow(interview: Record<string, unknown>): boolean {
  return interview["withdrawn"] === true;
}

function Section({ section, packet }: { section: PacketSection; packet: PacketOpenOutput }) {
  const body = (() => {
    switch (section) {
      case "appAccess":
        return <TextLink href="#interviews">{NAV_ITEM_LABELS.interviews}</TextLink>;
      case "packetLinks":
        return (
          // a column with room between the links, so each candidate's link is a 24 px target (WCAG 2.5.8)
          <Stack gap={12}>
            {packet.interviews.map((interview, i) => isWithdrawnRow(interview) ? (
              <Flex key={String(interview["interview_id"] ?? i)} gap={8}>
                <span>{String(interview["candidate"] ?? "")}</span>
                <Pill tone="neutral">{SCREEN_STATUS.withdrawn}</Pill>
              </Flex>
            ) : (
              <TextLink key={String(interview["interview_id"] ?? i)} href={`#interviews/${packet.series_id}/${String(interview["application_id"] ?? "")}`} hs={i === packet.interviews.findIndex((row) => !isWithdrawnRow(row)) ? "score.packet.open" : undefined}>
                {String(interview["candidate"] ?? "")}
              </TextLink>
            ))}
          </Stack>
        );
      case "assignedRootsSkills":
        return (
          <Stack gap={8}>
            <span>{packet.values_assigned.map((value) => <Pill key={value} tone="neutral">{value}</Pill>)}</span>
            <span>{skillWordsOf(packet).map((word, i) => <Pill key={packet.skill_numbers[i] ?? i} tone="neutral">{word}</Pill>)}</span>
          </Stack>
        );
      case "interviewGuidance":
        return <ul>{guidanceOf(packet).map((line, i) => <li key={i}>{line}</li>)}</ul>;
      case "scorecardSla":
        return (
          <ul>
            {packet.interviews.map((interview, i) => {
              const tier = dueTierOf(displayOf(interview), interview);
              const at = interview["due_at"];
              return tier && typeof at === "string"
                ? <li key={i}>{String(interview["candidate"] ?? "")} <Due tier={tier} phrase={SLA_LABELS.scorecardDue} at={at} /></li>
                : null;
            })}
          </ul>
        );
      case "confidentiality":
        return <p><b>{SCORECARD_PAGE_LABELS.independentFeedback}</b> {SCORECARD_PAGE_LABELS.noPeek}</p>;
    }
  })();
  return <Card title={SECTION_TITLE[section]} >{body}</Card>;
}

export function PacketPaneView({ packet }: { packet: PacketOpenOutput }) {
  const level = LEVELS.has(packet.level) ? LEVEL_DISPLAY[packet.level as Level] : null;
  return (
    <Stack gap={16}>
      <PageHeader
        title={SCORE_LABELS.interviewerPacket}
        pills={level ? <Pill tone="neutral">{level}</Pill> : undefined}
        meta={[packet.requisition_title, hisRoundLabel(packet.round_label)]}
      />
      {PACKET_SECTIONS.map((section) => <Section key={section} section={section} packet={packet} />)}
    </Stack>
  );
}

function PacketPaneBody({ packetId }: { packetId: string }) {
  const open = useRead(["packets", packetId], () => packetOpen({ packet_id: packetId }));
  if (open.isLoading) return <Card loading />;
  if (!open.data) return <EmptyState revoked>{SCORE_LABELS.interviewerPacket}</EmptyState>;
  return <PacketPaneView packet={open.data} />;
}

export function PacketPane({ packetId }: { packetId: string }) {
  return <McRoot><PacketPaneBody packetId={packetId} /></McRoot>;
}
